import { useState,useContext, useMemo, useEffect } from 'react';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ModalContainer,UIcon,IconContainer, MainContentContainer1, SubmitContainer, StyleButton, StyleInput, StyleLabel } from '../Modal/CreateAdminUser';
import { FilterDropDown } from '../../../../ui-components';
import { useSettingModal } from '../../../../hooks/Context';
import apiCall from '../../../../utils/apiCall';
import { NotificationContext } from '../../../../providers/notification';
import { ExNyRoute, PrRoute, ExRoute, AcRoute, MiRoute, CoRoute } from '../../../../utils/allRouteId';
import { useQuery } from '@tanstack/react-query';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';

const CreateTraining = () => {
  const TrainingId = 0;
  const [TrainingName, setTrainingName] = useState<string>('');
  const [TrainingVenue, setTrainingVenue] = useState<string>('');
  const [RouteId, setRouteId] = useState<number | null>(null);
  const [date, setDate] = useState<string>(''); // YYYY-MM-DD
  const [endDate, setEndDate] = useState<string>(''); // YYYY-MM-DD
  const [time, setTime] = useState<string>(''); // HH:mm
  const [loading, setLoading] = useState<boolean>(false);
  const [routeOptions, setRouteOptions] = useState<{ value: number; label: string }[]>([]);

  const { showNotification } = useContext(NotificationContext);
  const { setShowModal } = useSettingModal();
  const queryClient = useQueryClient();

  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        const response = await apiCall().get('/Routes/GetAllRoute');
        const data = response?.data?.Data || [];
        const formatted = data.map((route: any) => ({
          value: route.RouteId,
          label: route.RouteName,
        }));
        setRouteOptions(formatted);
      } catch (error) {
        console.error('Error fetching routes:', error);
      }
    };

    fetchRoutes();
  }, []);

  const handleCreateTraining = async () => {
    const TrainingDate = date && time ? `${date}T${time}:00` : null;
    const TrainingEndDate = endDate && time ? `${endDate}T${time}:00` : null;

    const data = {
      TrainingId,
      TrainingName,
      TrainingVenue,
      TrainingDate,
      TrainingEndDate,
      RouteId,
      IsActive: false,
    };

    setLoading(true);
    return (await apiCall().post('/SetUp/CreateUpdateTraining', data)).data;
  };

  const AddTrainingMutation = useMutation(handleCreateTraining, {
    onSuccess: () => {
      showNotification({
        type: 'success',
        message: 'Training Successfully Created',
      });
      setShowModal(false);
      setLoading(false);
      queryClient.invalidateQueries(['all-training']);
    },
    onError(error: any) {
      setShowModal(false);
      showNotification({
        type: 'error',
        message: error.message,
      });
      setLoading(false);
    },
  });

  const handleForm = (e: any) => {
    e.preventDefault();
    const currentDate = new Date();

    if (!TrainingName) {
      return showNotification({ type: 'error', message: 'Training name is required' });
    }

    if (!TrainingVenue) {
      return showNotification({ type: 'error', message: 'Training venue is required' });
    }

    if (!RouteId) {
      return showNotification({ type: 'error', message: 'Route Id is required' });
    }

    if (!date) {
      return showNotification({ type: 'error', message: 'Training Date is required' });
    }

    if (new Date(date) < currentDate) {
      return showNotification({ type: 'error', message: 'Training date cannot be in the past' });
    }

    if (!time) {
      return showNotification({ type: 'error', message: 'Training time is required' });
    }

    // ✅ Only validate if endDate is filled
    if (endDate) {
      const start = new Date(`${date}T${time}:00`);
      const end = new Date(`${endDate}T${time}:00`);
      if (end <= start) {
        return showNotification({
          type: 'error',
          message: 'End date must be after start date',
        });
      }
    }

    AddTrainingMutation.mutate();
  };

  return (
    <ModalContainer>
      <div>
        <div>
          <div>
            <IconContainer><UIcon /></IconContainer>
            <span>Training</span>
          </div>
        </div>
        <MainContentContainer1 onSubmit={handleForm}>
          <StyleLabel style={{ width: '100%' }}>
            <span>Membership Route</span>
            <FormControl fullWidth size="small">
              <Select
                labelId="route-select-label"
                value={RouteId ?? ''}
                onChange={(e) => setRouteId(e.target.value === '' ? null : Number(e.target.value))}
                displayEmpty
                sx={{
                  backgroundColor: '#11643C',
                  color: '#fff',
                  '& .MuiSvgIcon-root': { color: '#fff' },
                  '& .MuiSelect-select': { padding: '10px' },
                }}
              >
                <MenuItem value="">
                  <em>Select Route</em>
                </MenuItem>
                {routeOptions.length > 0 ? (
                  routeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No routes available</MenuItem>
                )}
              </Select>
            </FormControl>
          </StyleLabel>

          <StyleLabel>
            <span>Name</span>
            <StyleInput
              value={TrainingName}
              onChange={(e) => setTrainingName(e.target.value)}
              placeholder="Name"
            />
          </StyleLabel>

          <StyleLabel>
            <span>Venue</span>
            <StyleInput
              value={TrainingVenue}
              onChange={(e) => setTrainingVenue(e.target.value)}
              placeholder="Venue"
            />
          </StyleLabel>

          <StyleLabel>
            <span>Start Date</span>
            <StyleInput value={date} onChange={(e) => setDate(e.target.value)} type="date" />
          </StyleLabel>

          <StyleLabel>
            <span>End Date</span>
            <StyleInput value={endDate} onChange={(e) => setEndDate(e.target.value)} type="date" />
          </StyleLabel>

          <StyleLabel>
            <span>Time</span>
            <StyleInput value={time} onChange={(e) => setTime(e.target.value)} type="time" />
          </StyleLabel>

          <SubmitContainer>
            <span onClick={() => setShowModal(false)}>Cancel</span>
            <StyleButton type="submit">
              {!loading ? 'Submit' : 'Loading...'}
            </StyleButton>
          </SubmitContainer>
        </MainContentContainer1>
      </div>
    </ModalContainer>
  );
};

export default CreateTraining;

